import { useParams, Link, useNavigate } from "react-router-dom";
import { Col, Row, Button, ButtonGroup } from 'react-bootstrap';
import { useState, useEffect, useCallback } from 'react';

import ModalShell from '../common/modal-shell';
import { Toaster, createToast } from '../common/toasts';
import PreviousNext from '../common/previous-next';
import BasicSpinner from '../common/basic-spinner';
import AddChildren from '../common/add-children';
// import ListView from '../common/list-view';
import PaginatedList from '../common/pagination';

import ItemDetails from './item-details';
import ImageCreateForm from '../image/image-create-form';
import ItemUpdateForm from './item-update-form';

import { fetchUserImages, fetchUserItem, fetchUserItems, fetchUserItemImages, fetchUserItemParents } from '../../utils/fetch-helpers'
import { useAuth } from "../../utils/auth-provider";

//================================================================

export default function ItemPage(){

  const navigate = useNavigate();
  const user = useAuth();

  const headers = {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${user.token}`
  }

  const params = useParams();

  const [parents, setParents] = useState(null);
  const [item, setItem] = useState(null);
  const [updateModalShow, setUpdateModalShow] = useState(false);

  const [itemImages, setItemImages] = useState(null);
  const [allImages, setAllImages] = useState(null);
  const [createImageModalShow, setCreateImageModalShow] = useState(false);
  const [addImageModalShow, setAddImageModalShow] = useState(false);

  const [toasts, setToasts] = useState([]);

  //----------------------------------------------------------------

  const fetchItem = useCallback(async (itemId, signal) => {
    setItem(await fetchUserItem(user, itemId, signal));
  }, [user, params.itemId]);

  //----------------------------------------------------------------

  const fetchAllImages = useCallback( async (signal) => {
    setAllImages(await fetchUserImages(user, signal));
  }, [user]);

  //----------------------------------------------------------------

  const fetchParents = useCallback(async (itemId, signal) => {
    setParents(await fetchUserItemParents(user, itemId, signal));
  }, [user]);

  //----------------------------------------------------------------

  const fetchItemImages = useCallback(async (itemId, signal) => {
    const fetchResult = await fetchUserItemImages(user, itemId, signal);
    setItemImages(fetchResult['images']);
  },[user]);

  //----------------------------------------------------------------

  const addImageToItem = useCallback(async (image) => {
    fetch(`/api/item/${params.itemId}`, {
      method: 'PUT',
      body: JSON.stringify({'add_images':[image._id]}),
      headers: headers
    })
    .then(async (response) => {
      if(response.ok){
        setItem(await response.json());
      } else {
        setToasts([...toasts, createToast(
          "Failed to add image to item",
          `${response.status}: ${response.json()}`
        )]);
      }
    })
    .catch((error) => {
      setToasts([...toasts, createToast(
        "Error",
        `While adding images to item ${params.itemId}: ${error.message}`
      )]);
    });
  }, [toasts]);

  //----------------------------------------------------------------

  const addImagesToItem = useCallback(async (images) => {
    if (!item) return;

    await fetch(`/api/item/${item._id}`, {
      method: 'PUT',
      body: JSON.stringify({'add_images': images}),
      headers: headers
    })
    .then(async (response) => {
      const result = await response.json();
      if(response.ok){
        setItem(result);
      } else {
        setToasts([...toasts, createToast(
          "Failed to add images to item",
          `${response.status} - ${JSON.stringify(result, null, "\t")}`
        )]);
      }
    })
    .catch((error) => {
      setToasts([...toasts, createToast(
        "Error",
        `While adding images ${images} to item ${item._id}: ${error.message}`
      )]);
    });
  }, [item, toasts]);

  //----------------------------------------------------------------

  const removeImageFromItem = useCallback(async (imageId) => {

    await fetch(`/api/item/${params.itemId}`, {
      method: 'PUT',
      body: JSON.stringify({'remove_images' : [imageId]}),
      headers: headers
    })
    .then(async (response) => {
      if(response.ok){
        setItem(await response.json());
      } else {
        setToasts([...toasts, createToast(
          "Failed to remove image from item",
          `${response.status}: ${response.json()}`
        )]);
      }
    })
    .catch((error) => {
      setToasts([...toasts, createToast(
        "Error",
        `While removing images from item ${params.itemId}: ${error.message}`
      )]);
    });
  }, [itemImages, toasts]);

//----------------------------------------------------------------

  useEffect(() => {
    if (!item) return;

    const abortController = new AbortController();
    const signal = abortController.signal;

    fetchParents(item._id, signal);
    fetchItemImages(item._id, signal);

    return () => abortController.abort();
  }, [item]);

  //----------------------------------------------------------------

  useEffect(() => {

    const abortController = new AbortController();
    const signal = abortController.signal;

    fetchItem(params.itemId, signal);
    fetchAllImages(signal);

    return () => abortController.abort();
  }, [params.itemId]);

  //----------------------------------------------------------------

  return(
    <>
      {/*<PreviousNext
        objects={items}
        currentObject={item}
        onClick={setCurrentItem}
        basePath={`/dashboard/item`}
      />*/}
      { !item ?
        <BasicSpinner className="mb-4 col text-center" text={'Loading...'} />
        :
        <>
          <Row className="mb-2 align-items-center">
            <Col className="h4 text-truncate" sm={9}>
              <b>{"Item: "}</b>{item.name || ""}
            </Col>
            <Col sm={3}>
              <Button
                className="text-nowrap text-truncate w-100"
                onClick={() => setUpdateModalShow(true)}
                disabled={!item}
              >
                Edit
              </Button>
            </Col>
          </Row>

          <ItemDetails item={item}/>

          <Row className='mb-2 align-items-center'>
            <Col>
              <Button
                className="w-100"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </Col>
            <Col>
              <Button
                className="w-100"
                onClick={() => navigate(1)}
              >
                Forward
              </Button>
            </Col>
          </Row>

          { itemImages &&
            <>
              <hr />
              <Row className="mb-3 align-items-center">
                <Col className="h4 m-auto text-start col">Images</Col>
                <Col className=" my-auto text-end col-2">Add</Col>
                <ButtonGroup className="col-md-3 text-center">
                  <Button
                    onClick={() => setCreateImageModalShow(true)}
                  >
                    New
                  </Button>
                  <Button
                    onClick={() => setAddImageModalShow(true)}
                  >
                    Existing
                  </Button>
                </ButtonGroup>
              </Row>
              <PaginatedList
                key="child-images"
                list={itemImages}
                type="image"
                buttonCallback={removeImageFromItem}
                buttonIcon='dash'
                buttonRounded='1'
                isPrivate
              />
            </>
          }

          { parents && parents['collections'].length > 0 &&
            <>
              <hr />
              <Row className="mb-3 align-items-center">
                <Col className="h4 my-0 text-start">Parent Collections:</Col>
              </Row>

              <PaginatedList
                key="parent-collections"
                list={parents.collections}
                type="collection"
                accessToken={user.token}
                isPrivate
              />
            </>
          }

          <ModalShell
            title="Add New Image"
            show={createImageModalShow}
            onHide={() => setCreateImageModalShow(false)}
            hideFooter={true}
          >
            <ImageCreateForm
              onCancel={() => setCreateImageModalShow(false)}
              onCreate={(image) => {
                addImageToItem(image);
                setCreateImageModalShow(false)
              }}
              parent={item}
            />
          </ModalShell>

          <ModalShell
            key="addImageModal"
            title="Add Existing Images(s)"
            show={addImageModalShow}
            onHide={() => setAddImageModalShow(false)}
            hideFooter={true}
          >
            <AddChildren
              parent={item}
              childList={allImages && item ? allImages.filter(child => !item.images.includes(child._id)) : []}
              onAdd={(imageList) => {
                addImagesToItem(imageList);
                setAddImageModalShow(false);
              }}
              type="image"
            />
          </ModalShell>

          <ModalShell
            title="Update Item"
            show={updateModalShow}
            onHide={() => setUpdateModalShow(false)}
            hideFooter={true}
          >
            <ItemUpdateForm
              item={item}
              onCancel={() => setUpdateModalShow(false)}
              onUpdate={setItem}
            />
          </ModalShell>
        </>
      }

      <Toaster toasts={toasts} />

    </>
  );
}
